import { DataClientError } from "../errors";
import { LeadModel } from "@blazeo.com/calendar-client";
import storage from "@/services/storage";
import dropdownEnums from "@/services/dropdown-enums";


/** Blazeo API column → CRM grid column(s). Single source of truth. */
const BLAZEO_CRM_COLUMNS = {
  id: ["Id"],
  lead_id: ["LeadId"],
  email: ["Email"],
  name: ["Name"],
  first_name: ["FirstName"],
  last_name: ["LastName"],
  salutation: ["Salutation"],
  phone: ["Phone"],
  company_key: ["CompanyKey"],
  source: ["LeadSourceId"],
  lead_type: ["LeadTypeId"],
  referrer_link: ["ReferrerLink"],
  created_on: ["CreatedOn"],
  modified_on: ["ModifiedOn"],
};

const CRM_TO_BLAZEO = Object.entries(BLAZEO_CRM_COLUMNS).reduce(
  (acc, [blazeo, crmCols]) => {
    crmCols.forEach((crm) => {
      acc[crm.toLowerCase()] = blazeo;
    });
    return acc;
  },
  {}
);

export class SmartHubLeadsAdapter {
  constructor() {
    this.LeadModel = LeadModel;
    this.pendingRequests = new Map();
  }

  async getGridItems(params, context = {}) {
    try {
      const companyKey = context.companyKey;
      if (!companyKey) {
        throw new Error("companyKey is required in context for Blazeo getGridItems");
      }

      const payload = {
        skip: params?.skip || 0,
        take: params?.take || 25,
        filters: this._extractFilters(params?.where),
        ...this._extractOrdering(params?.order),
      };

      const cacheKey = JSON.stringify({ companyKey, payload });
      if (this.pendingRequests.has(cacheKey)) {
        return this.pendingRequests.get(cacheKey);
      }

      const requestPromise = (async () => {
        const leadSearchRequest = context.isLeadSearch ? this.LeadModel.searchByCompany : this.LeadModel.getByCompany;
        const response = await leadSearchRequest(companyKey, payload);
        const rawItems = Array.isArray(response) ? response : (response?.leads || response?.items || response?.data || []);
        const totalCount = response?.totalCount !== undefined ? response.totalCount : rawItems.length;

        const items = this._mapToCRMFormat(rawItems);

        return {
          leadsGrid: {
            items,
            totalCount
          }
        };
      })();

      this.pendingRequests.set(cacheKey, requestPromise);

      try {
        return await requestPromise;
      } finally {
        this.pendingRequests.delete(cacheKey);
      }
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message || "Blazeo API Error", error);
    }
  }



  async getLeadDetails(id, context = {}) {
    try {
      // In CRM, the ID might be numerical or UUID from Blazeo. 
      // If we only have numeric IDs on UI, we must map them. For now, assuming id is leadId.
      let lead;
      if (context.companyKey && context.email) {
          lead = await this.LeadModel.getByEmail(context.email, context.companyKey);
      } else {
          lead = await this.LeadModel.get(String(id));
      }

      return {
          leadDetails: [this._mapSingleToCRMFormat(lead)]
      };
    } catch (error) {
       throw new DataClientError("blazeo", 500, error.message || "Blazeo API Error", error);
    }
  }

  _isNotFoundError(error) {
    const status =
      error?.status ?? error?.response?.status ?? error?.statusCode;
    if (status === 404) return true;
    const message = String(error?.message || "").toLowerCase();
    return message.includes("not found") || message.includes("404");
  }

  async getLeadByEmail(email, context = {}) {
    try {
      const { companyKey } = context;
      if (!companyKey) throw new Error("companyKey is required for getLeadByEmail");
      const lead = await this.LeadModel.getByEmail(email, companyKey);
      return this._mapSingleToCRMFormat(lead);
    } catch (error) {
      if (this._isNotFoundError(error)) return null;
      throw new DataClientError("blazeo", 500, error.message || "Blazeo API Error", error);
    }
  }

  async validateLeadField(name, value, context = {}) {
    try {
      const { companyKey } = context;
      if (!companyKey) throw new Error("companyKey is required for validateLeadField");
      
      if (name === "email") {
        return await this.getLeadByEmail(value, context);
      } else if (name === "phone") {
        let countryCode = (context.countryCode || "").trim();
        if (countryCode && !countryCode.startsWith("+")) {
          countryCode = `+${countryCode}`;
        }
        const fullPhone = `${countryCode}${value}`;
        const lead = await this.LeadModel.getByPhone(fullPhone, companyKey);
        if(lead?.Phone) {
          lead.phone = lead?.Phone;
        }
        return this._mapSingleToCRMFormat(lead);
      }
      return null;
    } catch (error) {
      if (this._isNotFoundError(error)) return null;
      throw new DataClientError("blazeo", 500, error.message || "Blazeo API Error", error);
    }
  }

  _extractFilters(where) {
    if (!where || typeof where !== 'object') return [];
    const filters = [];

    const traverse = (obj) => {
      for (const key in obj) {
        if (key === 'or' || key === 'and') {
          if (Array.isArray(obj[key])) {
            obj[key].forEach(traverse);
          }
        } else {
          const condition = obj[key];
          if (condition && typeof condition === 'object') {
            const searchText = condition?.contains || condition?.startsWith || condition?.eq || condition?.containsCaseInsensitive;
            if (searchText !== undefined && searchText !== null && searchText !== '') {
              let mappedCol = this._mapColumnToBlazeo(key);
              if (mappedCol) {
                let textVal = String(searchText);
                if (mappedCol === 'lead_type') {
                  const mappedType = this._mapLeadTypeToBlazeo(searchText);
                  if (mappedType) {
                    textVal = mappedType;
                  }
                }
                filters.push({ column: mappedCol, text: textVal });
              }
            } else {
              const dateVal = condition?.gte || condition?.lte || condition?.gt || condition?.lt;
              if (dateVal) {
                let mappedCol = this._mapColumnToBlazeo(key);
                if (mappedCol) {
                  const operator = Object.keys(condition)[0];
                  let textVal = String(dateVal);
                  if (mappedCol === 'created_on') {
                    textVal = textVal.split(/[\sT]/)[0];
                    filters.push({ column: mappedCol, text: textVal, originalOperator: operator });
                  } else {
                    filters.push({ column: mappedCol, text: textVal, operator });
                  }
                }
              }
            }
          }
        }
      }
    };

    traverse(where);

    // Pick the best created_on filter (prefer lte/lt for the selected day end-bound) and clean it
    const createdOnFilters = filters.filter(f => f.column === 'created_on');
    let finalFilters = filters.filter(f => f.column !== 'created_on');
    if (createdOnFilters.length > 0) {
      let selectedCreatedOn = createdOnFilters.find(f => f.originalOperator === 'lte' || f.originalOperator === 'lt');
      if (!selectedCreatedOn) {
        selectedCreatedOn = createdOnFilters[0];
      }
      finalFilters.push({ column: 'created_on', text: selectedCreatedOn.text });
    }

    const seen = new Set();
    return finalFilters.filter(f => {
      const cacheKey = `${f.column}:${f.text}:${f.operator || ''}`;
      if (seen.has(cacheKey)) return false;
      seen.add(cacheKey);
      return true;
    });
  }

  _extractSearch(where) {
    if (!where || typeof where !== 'object') return {};

    const findSearch = (obj) => {
      for (const key in obj) {
        if (key === 'or' || key === 'and') {
          if (Array.isArray(obj[key])) {
            for (const item of obj[key]) {
              const result = findSearch(item);
              if (result) return result;
            }
          }
        } else {
          const condition = obj[key];
          const searchText = condition?.contains || condition?.startsWith || condition?.eq || condition?.containsCaseInsensitive;
          if (searchText) {
            let searchColumn = key;
            if (searchColumn.toLowerCase() === 'firstname' || searchColumn.toLowerCase() === 'lastname') {
              searchColumn = 'Name';
            }
            if (searchColumn === 'leadSourceId') {
              searchColumn = 'Source';
            }
            return { searchColumn, searchText };
          }
        }
      }
      return null;
    };

    const search = findSearch(where);
    return search ? { searchColumn: search.searchColumn, searchText: search.searchText } : {};
  }

  _extractOrdering(order) {
    if (!order || !order.length) return {};
    const sortObj = order[0];
    return {
      sortBy: Object.keys(sortObj)[0],
      sortOrder: Object.values(sortObj)[0]
    };
  }

  async createLead(crmPayload, context = {}) {
    try {
      const { companyKey } = context;
      if (!companyKey) throw new Error("companyKey is required for createLead");

      const mapped = this._mapToBlazeoFormat(crmPayload, companyKey);
      const response = await this.LeadModel.createLead({
        companyKey,
        email: mapped.email,
        phone: mapped.phone,
        firstName: mapped.firstName,
        lastName: mapped.lastName,
        salutation: mapped.salutation,
        source: mapped.source,
        leadType: mapped.leadType || "sales",
        description: mapped.description,
      });

      if (response?.status === "failure") {
        throw new DataClientError(
          "blazeo",
          400,
          response.message || "Failed to create lead.",
        );
      }

      const mappedLead = this._mapSingleToCRMFormat(response);
      if (!mappedLead?.id && !mappedLead?.leadId) {
        throw new DataClientError(
          "blazeo",
          500,
          "Lead could not be created.",
        );
      }
      return mappedLead;
    } catch (error) {
      if (error instanceof DataClientError) throw error;
      throw new DataClientError("blazeo", 500, error.message || "Create Lead Error", error);
    }
  }

  async updateLead(crmPayload, context = {}) {
    try {
      const { companyKey } = context;
      if (!companyKey) throw new Error("companyKey is required for updateLead");

      const blazeoPayload = this._mapToBlazeoFormat(crmPayload, companyKey);
      const response = await this.LeadModel.updateLead(blazeoPayload);
      return this._mapSingleToCRMFormat(response);
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message || "Update Lead Error", error);
    }
  }

  async deleteLead(leadIdOrParams, context = {}) {
    try {
      const { companyKey } = context;
      let payload;
      
      if (leadIdOrParams && typeof leadIdOrParams === "object") {
        payload = {
          ...leadIdOrParams,
          companyKey: leadIdOrParams.companyKey || companyKey,
        };
      } else if (typeof leadIdOrParams === "string" && leadIdOrParams.includes("@")) {
        payload = {
          email: leadIdOrParams,
          companyKey,
        };
      } else {
        payload = {
          leadId: leadIdOrParams,
          companyKey,
        };
      }

      const response = await this.LeadModel.deleteLead(payload);
      return response;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message || "Delete Lead Error", error);
    }
  }

  _mapToBlazeoFormat(crmPayload, companyKey) {
    // Handle both direct lead object or full formData structure
    const crmLead = crmPayload.lead || crmPayload;
    const contact = crmLead.contact || {};
    
    let countryCode = (contact.countryCode || "").trim();
    let phone = (contact.phone || "").trim();

    // Ensure countryCode starts with '+'
    if (countryCode && !countryCode.startsWith("+")) {
      countryCode = `+${countryCode}`;
    }

    // Clean phone number of spaces/dashes for comparison
    const cleanedPhone = phone.replace(/[\s-()]/g, "");

    let finalPhone = "";

    if (cleanedPhone) {
      if (countryCode) {
        const codeDigits = countryCode.replace("+", ""); // e.g. "1"
        
        if (cleanedPhone.startsWith(countryCode)) {
          finalPhone = cleanedPhone;
        } else if (cleanedPhone.startsWith(`+${codeDigits}`)) {
          finalPhone = cleanedPhone;
        } else if (cleanedPhone.startsWith(codeDigits)) {
          finalPhone = `+${cleanedPhone}`;
        } else {
          finalPhone = `${countryCode}${cleanedPhone}`;
        }
      } else {
        if (cleanedPhone.length === 11 && cleanedPhone.startsWith("1")) {
          finalPhone = `+${cleanedPhone}`;
        } else if (!cleanedPhone.startsWith("+")) {
          finalPhone = `+1${cleanedPhone}`;
        }
      }
    }

    const firstName = contact.firstName || crmLead.firstName || "";
    const lastName = contact.lastName || crmLead.lastName || "";

    return {
      leadId: crmLead.LeadId || crmLead.leadId || null,
      firstName: firstName,
      lastName: lastName,
      salutation: contact.salutation || "",
      email: contact.email || "",
      countryCode: contact.countryCode || "",
      phone: finalPhone,
      source: (crmLead.leadSourceId && String(crmLead.leadSourceId).toLowerCase() !== "none") ? crmLead.leadSourceId : "none",
      company_key: companyKey,
      leadType: this._mapLeadTypeToBlazeo(crmLead.leadTypeId),
      description: crmLead.description || ""
    };
  }

  _mapToCRMFormat(blazeoLeads) {
      if (!Array.isArray(blazeoLeads)) return [];
      return blazeoLeads.map(l => this._mapSingleToCRMFormat(l)).filter(Boolean);
  }
  
  _getLeadSourceValue(lead = {}) {
    return (
      lead.source ??
      lead.Source ??
      lead.leadSource ??
      lead.LeadSource ??
      ""
    );
  }

  _mapSingleToCRMFormat(lead) {
      if (!lead) return null;
      let firstName = lead.firstName || lead.first_name || "";
      let lastName = lead.lastName || lead.last_name || "";
      const salutation = lead.salutation || "";
      const sourceValue = this._getLeadSourceValue(lead);
      
      if (!firstName && !lastName && lead.name) {
          const parts = lead.name.split(" ");
          firstName = parts[0] || "";
          lastName = parts.slice(1).join(" ");
      }

    const mappedLead = {
      // Provide both casing to satisfy grid metadata and internal hooks
      Id: lead.id || lead.leadId,
      id: lead.id || lead.leadId,
      LeadId: lead.leadId,
      leadId: lead.leadId,
      Salutation: salutation,
      salutation: salutation,
      FirstName: firstName,
      firstName: firstName,
      LastName: lastName,
      lastName: lastName,
      Email: lead.email,
      email: lead.email,
      Phone: lead.phone,
      phone: lead.phone,
      source: sourceValue,
      Source: sourceValue,
      LeadSource: sourceValue,
      leadSourceId: (() => {
        if (!sourceValue) return "";
        const basicSources = (dropdownEnums?.enum_BasicLeadSource || []).map(s => s.value);
        const matched = basicSources.find(
          (s) => s.toLowerCase() === String(sourceValue).toLowerCase()
        );
        return matched || sourceValue;
      })(),
      leadTypeId: this._mapLeadTypeToCRM(lead.leadType),
      leadType: this._mapLeadTypeToCRM(lead.leadType),
      CreatedOn: lead.createdOn,
      createdOn: lead.createdOn,
      ModifiedOn: lead.modifiedOn,
      modifiedOn: lead.modifiedOn,
      isNew: lead.isNew || lead.IsNew || false,
      state: sourceValue || "",
      assignedTo: lead.assignedTo || lead.assignedToId || lead.assigneeId || null,
      description: lead.description || ""
    };
    return mappedLead
  }

  _mapLeadTypeToCRM(type) {
    if (type === undefined || type === null) return null;
    
    // Normalize numeric string to actual number
    let normalizedType = type;
    if (typeof type === "string" && !isNaN(Number(type)) && type.trim() !== "") {
      normalizedType = Number(type);
    }
    
    // Blazeo maps 'sales' -> 0, 'service' -> 1, 'others' -> 2, 'none' -> 3
    // CRM expects Sales -> 1, Service -> 2, Others -> 3, None -> ""
    if (normalizedType === 0) return 1; // Sales
    if (normalizedType === 1) return 2; // Service
    if (normalizedType === 2) return 3; // Others
    if (normalizedType === 3) return ""; // None
    
    if (typeof normalizedType === "string") {
      const lower = normalizedType.toLowerCase();
      if (lower === "sales") return 1;
      if (lower === "service") return 2;
      if (lower === "others") return 3;
      if (lower === "none") return "";
    }
    return null;
  }

  _mapLeadTypeToBlazeo(type) {
    if (type === 1 || type === "1") return "sales";
    if (type === 2 || type === "2") return "service";
    if (type === 3 || type === "3") return "others";
    return "none";
  }

  _mapColumnToBlazeo(column) {
    return CRM_TO_BLAZEO[String(column).trim().toLowerCase()] || null;
  }

  _mapColumnsFromBlazeo(columns = []) {
    const seen = new Set();
    return columns.flatMap((column) => {
      const mapped =
        BLAZEO_CRM_COLUMNS[String(column).trim().toLowerCase()] || [];
      return mapped.filter((col) => col && !seen.has(col) && seen.add(col));
    });
  }

  _mapColumnsToBlazeo(columns = []) {
    const allowed = new Set(this.LeadModel.allowedColumns || []);
    const seen = new Set();
    return columns
      .map((column) => this._mapColumnToBlazeo(column))
      .filter(
        (column) => column && allowed.has(column) && !seen.has(column) && seen.add(column)
      );
  }

  async getColumnSelection(userId) {
    try {
      const response = await this.LeadModel.getColumnSelection(String(userId));
      if (response?.status !== "success") return null;

      let rawColumns = response?.data?.columns;
      if (!Array.isArray(rawColumns)) {
        rawColumns = [];
      } else {
        rawColumns = [...rawColumns];
      }

      // Forcefully ensure first_name is included in retrieved columns
      if (!rawColumns.some(c => String(c).toLowerCase() === "first_name")) {
        rawColumns.push("first_name");
      }

      const columns = this._mapColumnsFromBlazeo(rawColumns);
      return columns.length ? { columns } : null;
    } catch (error) {
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Failed to load column selection",
        error
      );
    }
  }

  async saveColumnSelection(userId, columns) {
    try {
      const cols = Array.isArray(columns) ? [...columns] : [];
      // Forcefully ensure FirstName is in the mapped columns list
      if (!cols.some(c => String(c).toLowerCase() === "firstname")) {
        cols.push("FirstName");
      }

      const blazeoColumns = this._mapColumnsToBlazeo(cols);
      // Forcefully ensure first_name is included in the backend columns list
      if (!blazeoColumns.some(c => String(c).toLowerCase() === "first_name")) {
        blazeoColumns.push("first_name");
      }

      if (!blazeoColumns.length) {
        throw new DataClientError("blazeo", 400, "At least one valid column is required");
      }
      const response = await this.LeadModel.saveColumnSelection(
        String(userId),
        blazeoColumns
      );
      if (response?.status !== "success") {
        throw new DataClientError(
          "blazeo",
          400,
          response?.message || "Failed to save column selection",
          response
        );
      }
      return response;
    } catch (error) {
      if (error instanceof DataClientError) throw error;
      throw new DataClientError(
        "blazeo",
        500,
        error.message || "Failed to save column selection",
        error
      );
    }
  }

  async exportLeads(params, context = {}) {
    try {
      const { companyKey } = context;
      if (!companyKey) throw new Error("companyKey is required for exportLeads");
      
      const userEmail = params?.userEmail || context?.userEmail || storage.getItemFromParsedStorage("Profile", "Email") || storage.getItemFromParsedStorage("Profile", "SSOEmail");
      const options = {
        userEmail
      };
      const response = await this.LeadModel.requestExport(companyKey, options);
      return response;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message || "Export Leads Error", error);
    }
  }
}
